import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/providers";
import { AuthAPI, setSessionToken } from "@/lib/api";

export default function SuperAdminLoginModal({ open, onClose }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setBusy(true);
    try {
      const { data } = await AuthAPI.superAdminLogin(password);
      // Persist token for cross-origin / cookie-blocked deployments — same
      // belt-and-suspenders pattern as the Google sign-in flow.
      if (data?.session_token) setSessionToken(data.session_token);
      if (refreshUser) await refreshUser();
      toast.success("Logged in as super-admin");
      setPassword("");
      onClose?.();
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="super-admin-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <form
        onSubmit={onSubmit}
        className="v-surface"
        style={{
          width: "min(440px, 100%)",
          padding: 28,
          background: "var(--v-bg)",
          border: "1px solid var(--v-border)",
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="super-admin-modal-close"
          style={{
            position: "absolute", top: 12, right: 12,
            background: "transparent", border: "none", color: "var(--v-text-2)",
            fontSize: 18, cursor: "pointer",
          }}
        >×</button>
        <div className="overline">Super-admin access</div>
        <h2 style={{ fontSize: 24, letterSpacing: "-.01em", marginTop: 6 }}>
          Bypass Google.
        </h2>
        <p style={{ marginTop: 6, color: "var(--v-text-2)", fontSize: 13, lineHeight: 1.55 }}>
          Studio-only emergency login. Enter the shared super-admin password
          to access the studio dashboard.
        </p>

        <div style={{ marginTop: 22 }}>
          <div className="overline">Password</div>
          <input
            type="password"
            className="v-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="off"
            autoFocus
            data-testid="super-admin-password-input"
          />
        </div>

        <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" className="v-btn v-btn-outline" onClick={onClose} data-testid="super-admin-cancel-btn">Cancel</button>
          <button
            type="submit"
            className="v-btn"
            disabled={busy || !password.trim()}
            data-testid="super-admin-submit-btn"
          >
            {busy ? "Unlocking…" : "Unlock dashboard →"}
          </button>
        </div>

        <p style={{ marginTop: 18, fontSize: 11, color: "var(--v-text-2)", lineHeight: 1.55 }}>
          This is a shared password. Rotate it from
          <span className="mono"> Settings → Super-admin password </span>
          after every change of guard.
        </p>
      </form>
    </div>
  );
}
